import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../../services/authService';
import './Auth.css';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authService.forgotPassword(email);
      setSubmitted(true);
      toast.success('Password reset link sent to your email');
    } catch (error) {
      toast.error(error.error?.message || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="auth-page">
        {/* Left – Branding */}
        <div className="auth-left">
          <div className="auth-brand">
            <div className="auth-brand-icon">
              <span className="material-symbols-outlined">work</span>
            </div>
            <span className="auth-brand-name">HiringOpen</span>
          </div>
          <h1 className="auth-left-headline">Almost<br />There</h1>
          <p className="auth-left-sub">Follow the instructions in your inbox to reset your password and get back to hiring.</p>
          <div className="auth-features">
            {[
              { icon: 'lock', title: 'Secure Reset', desc: 'One-time code delivered to your inbox' },
              { icon: 'schedule', title: 'Expires in 10 min', desc: 'Reset links are time-sensitive for safety' },
              { icon: 'email', title: 'Instant Email', desc: 'Check your inbox for the 6-digit OTP' },
              { icon: 'restore', title: 'Quick Recovery', desc: 'Reset and sign in again in seconds' },
            ].map(f => (
              <div key={f.title} className="auth-feature">
                <div className="auth-feature-icon">
                  <span className="material-symbols-outlined">{f.icon}</span>
                </div>
                <div className="auth-feature-text">
                  <h4>{f.title}</h4>
                  <p>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right – Success */}
        <div className="auth-right">
          <Link to="/" className="auth-back-link">
            <span className="material-symbols-outlined">arrow_back</span>
            Back to Home
          </Link>
          <div className="auth-form-wrap">
            <div className="auth-success-icon">
              <span className="material-symbols-outlined">mark_email_read</span>
            </div>
            <h1 className="auth-form-title">Check Your Email</h1>
            <p className="auth-form-subtitle">
              We've sent a 6-digit OTP to <strong>{email}</strong>
            </p>

            <div className="auth-success-info">
              <span className="material-symbols-outlined">info</span>
              <p>The OTP will expire in 10 minutes.</p>
            </div>

            <div className="auth-success-actions">
              <Link
                to={`/reset-password?email=${encodeURIComponent(email)}`}
                className="auth-submit-btn"
                style={{ textDecoration: 'none', marginBottom: '12px' }}
              >
                <span className="material-symbols-outlined">key</span>
                Reset Password Now
              </Link>
              <Link to="/login" className="auth-text-link">
                <span className="material-symbols-outlined">arrow_back</span>
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      {/* Left – Branding */}
      <div className="auth-left">
        <div className="auth-brand">
          <div className="auth-brand-icon">
            <span className="material-symbols-outlined">work</span>
          </div>
          <span className="auth-brand-name">HiringOpen</span>
        </div>
        <h1 className="auth-left-headline">Forgot<br />Password?</h1>
        <p className="auth-left-sub">No worries — enter your registered email and we'll send you a one-time code to reset it.</p>
        <div className="auth-features">
          {[
            { icon: 'lock', title: 'Secure Reset', desc: 'One-time code delivered to your inbox' },
            { icon: 'schedule', title: 'Expires in 10 min', desc: 'Reset links are time-sensitive for safety' },
            { icon: 'email', title: 'Instant Email', desc: 'Check your inbox for the 6-digit OTP' },
            { icon: 'restore', title: 'Quick Recovery', desc: 'Reset and sign in again in seconds' },
          ].map(f => (
            <div key={f.title} className="auth-feature">
              <div className="auth-feature-icon">
                <span className="material-symbols-outlined">{f.icon}</span>
              </div>
              <div className="auth-feature-text">
                <h4>{f.title}</h4>
                <p>{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right – Form */}
      <div className="auth-right">
        <Link to="/" className="auth-back-link">
          <span className="material-symbols-outlined">arrow_back</span>
          Back to Home
        </Link>
        <div className="auth-form-wrap">
          <div className="auth-form-icon">
            <span className="material-symbols-outlined">lock_reset</span>
          </div>
          <h1 className="auth-form-title">Reset Password</h1>
          <p className="auth-form-subtitle">Enter your registered email to receive the OTP</p>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-field">
              <label className="auth-label">
                <span className="material-symbols-outlined">email</span>
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="auth-input"
                placeholder="john@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="auth-spinner"></div>
                  Sending…
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined">send</span>
                  Send OTP
                </>
              )}
            </button>
          </form>

          <div className="auth-bottom-link">
            Remember your password? <Link to="/login">Sign In</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
